'use client';

import { useRef, useState } from 'react';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { Card, Typography, Image, CardContent, Box, Dialog } from '@packages/shared';
import { useCookieConsent } from '@packages/shared/src/hooks/useCookieConsent/useCookieConsent';
import { useIntl } from 'react-intl';
import { LoadbeeFragmentFragmentDoc } from '@packages/gql/generated/shopping/LoadbeeFragmentFragmentDoc';
import { loadBeeCardMessages } from './messages';
import type { Vendor } from '../Tracking/hooks/useManufacturerTracking';
import { useManufacturerTracking } from '../Tracking/hooks/useManufacturerTracking';

/* GraphQL */ `
  fragment LoadbeeFragment on Product {
    brand {
      image
      name
    }
    trackingBrand: brand {
      ...UseManufacturerTrackingFragment
    }
    # is needed for loadbee
    ean
    loadbee(locale: $locale) {
      url
    }
  }
`;

export type LoadbeeCardProps = {
  data: FragmentType<typeof LoadbeeFragmentFragmentDoc>;
};

const vendor: Vendor = 'Loadbee';

export const LoadbeeCard = ({ data }: LoadbeeCardProps) => {
  const isAllowed = useCookieConsent('C0004');
  const { formatMessage } = useIntl();

  const { brand, loadbee, trackingBrand } = getFragmentData(LoadbeeFragmentFragmentDoc, data);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const initialTracked = useRef(false);

  const { trackGlycerinButtonClick, trackGlycerinDisplayButton, trackGlycerinDisplayOverlay } =
    useManufacturerTracking();

  if (!isAllowed || !loadbee) {
    initialTracked.current = false;
    return null;
  }

  if (!initialTracked.current) {
    trackGlycerinDisplayButton({ data: trackingBrand, vendor });
    initialTracked.current = true;
  }

  if (isDialogOpen) {
    trackGlycerinDisplayOverlay({ data: trackingBrand, vendor });
  }

  const handleOnClick = () => {
    trackGlycerinButtonClick({ data: trackingBrand, vendor });
    setIsDialogOpen(true);
  };

  return (
    <Card>
      <CardContent
        onClick={handleOnClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {brand?.image && (
          <Box
            sx={{
              mr: 2,
            }}
          >
            <Image
              loading="lazy"
              src={brand.image}
              alt={brand.name}
              style={{ objectFit: 'contain' }}
              width={50}
              height={30}
            />
          </Box>
        )}
        <Typography variant="body1">
          {formatMessage(loadBeeCardMessages.labelText, {
            brand: brand?.name || '',
          })}
        </Typography>
      </CardContent>

      <Dialog
        open={isDialogOpen}
        handleClose={() => setIsDialogOpen(false)}
        size="xl"
        // TOOD: ask UX for fallback headline
        title={formatMessage(loadBeeCardMessages.modalHeadline, {
          brand: brand?.name || '',
        })}
        displayTrackingProps={null}
        dialogContent={<iframe title="title" src={loadbee.url} width="100%" height="100%" />}
        sx={{
          '.MuiDialogContent-root': {
            overflow: 'hidden',
          },
          '.MuiPaper-root': {
            height: '100%',
          },
        }}
      />
    </Card>
  );
};
